/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  Banner,
  ButtonExportCSVAsync,
  Card,
  CardAction,
  CardContent,
  CardExpiration,
  CardIcon,
  CardIconStatus,
  CardLabel,
  CardValue,
  DateRangePicker,
  Empty,
  EmptyTable,
  FieldWrapper,
  Filter,
  FilterButton,
  getExpirationStatus,
  HeaderCell,
  HeaderRow,
  Label,
  ModalFactory,
  PageActions,
  PageHeader,
  PageHeaderAction,
  PageHeaderAvatar,
  PageHeaderAvatarLayout,
  PageHeaderContent,
  PageTitle,
  PaginationFooter,
  Section,
  SectionHeader,
  Select,
  SortLabel,
  TableBody,
  TableHead,
} from 'components';
import { appConfig } from 'config/app';
import { DDOption } from 'config/const';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { getQueryStringValue } from 'hooks';
import PendingChimeLarge from 'images/illustrations/PendingChimeLarge.png';
import PendingChimeSmall from 'images/illustrations/PendingChimeSmall.png';
import PendingWalletLarge from 'images/illustrations/PendingWalletLarge.png';
import PendingWalletSmall from 'images/illustrations/PendingWalletSmall.png';
import { InviteService } from 'lib';
import { UserInvites } from 'modals';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { routeConstants, URL } from 'routes';
import { PaymentMethodAccountNumber } from 'screens/Settings/components';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Alerts, getUserAlertsRequest, getUserPayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts, selectUserAlert } from 'store/Collections';
import { Employers, getActionRequiredEmployersRequest, selectSitesForUser } from 'store/Employers';
import { clearInvite, Invites } from 'store/Invites';
import { getUserPayoutStatsRequest, selectPayoutStatsByStatus, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { acceptAllUserInvitesRequest, getCurrentUserCardRequest, getUserInvitesRequest, selectCurrentUser, User } from 'store/User';
import { selectUserInvites } from 'store/User/selectors';
import { AlertType, ApiQueryOperator, IApiQueryParam, IEmployer, IEmployerSite, InviteStatus, IPayout, PayoutStatus } from 'types';
import { getPaymentTypeIcon } from 'utils';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi, formatDateForUI, getTimeWithZone } from 'utils/date';
import { capitalize, toTitleCase } from 'utils/strings';
import * as API from '../../api';
import { EmployeePayoutCard, EmployeePayoutList, EmployeePayoutTable, EmployeePayoutTableRow, PendingBalance, PendingBalanceCard, StyledCardRow } from './components';
import EmployersTable from './EmployersTable';

const BannerLink = styled(Link)`
  color: ${theme.color.danger.light};
`;

const PayoutDateRange = styled.div`
  display: flex;
  align-items: center;

  .payout-date-range-picker {
    margin: 0 ${toRem(11)};
  }
`;

const EmployeeDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const DEFAULT_END_DATE = dayjs(new Date()).format('MM-DD-YYYY');
  const DEFAULT_START_DATE = dayjs(new Date()).subtract(29, 'day').format('MM-DD-YYYY');

  // UI State
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const { avatarUrl, cardDetail, firstName, lastName, card } = useSelector(selectCurrentUser());
  const userAlert = useSelector(selectUserAlert());
  const hasPaymentMethod = !!card;
  const isProcessing = hasPaymentMethod && (!cardDetail || cardDetail.id !== card?.id || !cardDetail.network);
  const activePaymentMethodType = cardDetail?.network;
  const activePaymentMethod = cardDetail && <PaymentMethodAccountNumber showIcon={false} cardType={activePaymentMethodType} cardNumber={cardDetail.last4} />;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_CURRENT_USER_REQUEST));
  const payoutStatsByStatus = useSelector(selectPayoutStatsByStatus());
  const isPayoutStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_USER_PAYOUT_STATS_REQUEST));
  const isPayoutsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_USER_PAYOUTS_REQUEST));
  const isCardDetailLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_CURRENT_USER_CARD_REQUEST));
  const isEmployersLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_EMPLOYERS_REQUEST));
  const isFetchingUserInvites = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_INVITES_REQUEST));
  const isAcceptingInvite = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.ACCEPT_INVITE_REQUEST));
  const isAcceptingUserInvites = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.ACCEPT_ALL_USER_INVITES_REQUEST));

  const userInvites = useSelector(selectUserInvites);
  const userInvitesAccepted = userInvites?.every((invite) => invite.status === InviteStatus.ACCEPTED);
  const invitesIgnored = InviteService.getInviteIgnored();
  const inviteToken = InviteService.getInviteToken();

  // Status selectors
  const alertStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Alerts.GET_USER_ALERTS_REQUEST));

  const [employersForUser, setEmployersForUser] = useState<Array<IEmployerSite | IEmployer>>([]);

  useEffect(() => {
    dispatch(getUserPayoutStatsRequest());
  }, []);

  useEffect(() => {
    if (inviteToken !== null) dispatch(clearInvite());
  }, [inviteToken]);

  useEffect(() => {
    if (card?.id && (!cardDetail || cardDetail.id !== card.id)) {
      dispatch(getCurrentUserCardRequest(card.id));
    }
  }, [dispatch, card, cardDetail]);

  useEffect(() => {
    if (!alertStatus) dispatch(getUserAlertsRequest());
  }, [dispatch, alertStatus]);

  useEffect(() => {
    dispatch(getActionRequiredEmployersRequest(setEmployersForUser));
  }, []);

  useEffect(() => {
    if (!invitesIgnored && !userInvites && !isFetchingUserInvites) dispatch(getUserInvitesRequest());
  }, [invitesIgnored, isFetchingUserInvites]);

  useEffect(() => {
    if (!invitesIgnored && !!userInvites?.length && !userInvitesAccepted) showInviteModal();
  }, [invitesIgnored, userInvites, userInvitesAccepted]);

  const fetchRecentPayouts = ({ sort, offset, limit, q }: IApiQueryOptions) => {
    dispatch(getUserPayoutsRequest({ limit, offset, sort, q }));
  };

  const fullName = `${firstName} ${lastName}`;
  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());

  const totalPaid = (() => {
    if (!payoutStatsByStatus) return 0;
    const elem = payoutStatsByStatus.find((item) => item.status === 'paid');
    if (!elem) return 0;
    return elem.net;
  })();

  const allPendingCount = (() => {
    if (!payoutStatsByStatus) return '--';
    const elem = payoutStatsByStatus.find((item) => item.status === 'pending');
    if (!elem) return 0;
    return elem.count;
  })();

  const allPendingTotal = (() => {
    if (!payoutStatsByStatus) return 0;
    const elem = payoutStatsByStatus.find((item) => item.status === 'pending');
    if (!elem) return 0;
    return elem.net;
  })();

  const allExpiredCount = (() => {
    if (!payoutStatsByStatus) return '--';
    const elem = payoutStatsByStatus.find((item) => item.status === 'expired');
    if (!elem) return 0;
    return elem.count;
  })();

  const allExpiredTotal = (() => {
    if (!payoutStatsByStatus) return 0;
    const elem = payoutStatsByStatus.find((item) => item.status === 'expired');
    if (!elem) return 0;
    return elem.net;
  })();

  const sitesForUser = useSelector(selectSitesForUser);

  const siteOptions = [
    { label: 'All', value: '' },
    ...sitesForUser.map((st: IEmployerSite) => ({
      label: st.name,
      value: st.id,
    })),
  ];

  const bannerMessage = userAlert ? `${toTitleCase(userAlert.label)} - ${userAlert.message}` : '';
  const alertLink = {
    [AlertType.APPROVAL]: <></>,
    [AlertType.CARD]: <Link to={routeConstants.private.settingsPaymentMethod}>{bannerMessage}</Link>,
    [AlertType.CONTACT]: (
      <a href={appConfig.LINKS.SUPPORT} target="_blank" rel="noopener noreferrer">
        {userAlert?.message}
      </a>
    ),
    [AlertType.EXPIRING]: <></>,
    [AlertType.INVITE_PENDING]: <></>,
  };

  const isExpired = cardDetail?.expDate ? getExpirationStatus(cardDetail?.expDate) === 'Expired' : false;

  const exportCSV = async (q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.Users.listPayouts({ q, sort, offset: 0, limit: 0 });
    return CSVFormatter.userPayouts(response.body?.data);
  };

  const onAcceptInviteSuccess = () => history.replace(routeConstants.private.acceptInvitation);
  const handleAcceptInvite = useCallback(() => dispatch(acceptAllUserInvitesRequest(userInvites, onAcceptInviteSuccess)), [userInvites]);

  const handleIgnoreInvite = () => {
    InviteService.setInviteIgnored(true);
    hideInviteModal();
  };

  const [showInviteModal, hideInviteModal] = useModal(
    () => (
      <ModalFactory customModal isActive closeOnOutSideClick={false} onClose={handleIgnoreInvite}>
        <UserInvites isActive userInvites={userInvites} handleAcceptInvite={handleAcceptInvite} handleIgnoreInvite={handleIgnoreInvite} onClose={handleIgnoreInvite} isLoading={isAcceptingUserInvites || isAcceptingInvite} />
      </ModalFactory>
    ),
    [userInvites, isAcceptingUserInvites, isAcceptingInvite]
  );

  return (
    <>
      <ListContextProvider
        fetchData={fetchRecentPayouts}
        totalSelector={() => payoutsTotal}
        defaultLimit={10}
        persistURL
        dataKey="employeeDashboardRecentPayments"
        defaultFilters={[
          {
            key: 'payment_date',
            operator: ApiQueryOperator.gte,
            value: formatDateForApi(DEFAULT_START_DATE),
          },
          {
            key: 'payment_date',
            operator: ApiQueryOperator.lte,
            value: formatDateForApi(DEFAULT_END_DATE),
          },
        ]}
      >
        {userAlert && (
          <Banner appearance="caution" data-cy="alert-banner">
            {alertLink[userAlert.type] || bannerMessage}
          </Banner>
        )}

        {isExpired && (
          <Banner appearance="danger" data-cy="expired-banner">
            <span>
              {'Your payment method has expired. '}
              <BannerLink to={routeConstants.private.settingsPaymentMethod}>Update or add a new one</BannerLink>
              {' to continue to receive payments.'}
            </span>
          </Banner>
        )}

        <PageHeader hasStats data-cy="page-header">
          <PageHeaderContent stackable>
            <PageHeaderAvatarLayout>
              <PageHeaderAvatar username={fullName} size="large" image={avatarUrl} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} />
              <PageTitle title={`Welcome back, ${firstName}`} />
            </PageHeaderAvatarLayout>

            <PageActions>
              <PageHeaderAction appearance="tertiary" label={hasPaymentMethod ? 'Edit Payment Method' : 'Add Payment Method'} to={URL.SettingsPaymentMethod} isLoading={isLoading} />
            </PageActions>
          </PageHeaderContent>

          <StyledCardRow data-cy="card-row">
            <Card>
              <CardIconStatus color={Appearance.SUCCESS} icon="Wallet" isLoading={isPayoutStatsLoading} />
              <CardContent data-cy="card-30-day-total">
                <CardLabel isLoading={isPayoutStatsLoading}>30 Day Total</CardLabel>
                <CardValue isLoading={isPayoutStatsLoading}>{centsToDollarsDisplay(totalPaid)}</CardValue>
              </CardContent>
            </Card>

            <Card>
              <CardIconStatus color={Appearance.WARNING} icon="Caution" isLoading={isPayoutStatsLoading} />
              <CardContent data-cy="card-pending-total">
                <CardLabel isLoading={isPayoutStatsLoading}>{`Pending Payments (${allPendingCount})`}</CardLabel>
                <CardValue isLoading={isPayoutStatsLoading}>{centsToDollarsDisplay(allPendingTotal)}</CardValue>
              </CardContent>
            </Card>

            <Card>
              <CardIconStatus color={Appearance.DANGER} icon="RemoveThick" isLoading={isPayoutStatsLoading} />
              <CardContent data-cy="card-expired-total">
                <CardLabel isLoading={isPayoutStatsLoading}>{`Expired Payments (${allExpiredCount})`}</CardLabel>
                <CardValue isLoading={isPayoutStatsLoading}>{centsToDollarsDisplay(allExpiredTotal)}</CardValue>
              </CardContent>
            </Card>

            <Card error={isExpired} data-cy={isExpired ? 'expired-card-wrapper' : undefined}>
              {isProcessing && !isCardDetailLoading && !isLoading ? (
                <>
                  <CardIconStatus color={Appearance.NEUTRAL} icon="Payment" isLoading={isLoading} />
                  <CardContent data-cy="card-payment-method">
                    <CardLabel isLoading={false}>Payment Method</CardLabel>
                    <CardValue isLoading={false}>
                      <>•••• •••• •••• ••••</>
                    </CardValue>
                  </CardContent>
                </>
              ) : (
                <>
                  {hasPaymentMethod ? (
                    <CardIcon bgColor={theme.color.neutral[100]} icon={getPaymentTypeIcon(activePaymentMethodType)} isLoading={isLoading} />
                  ) : (
                    <CardIconStatus color={Appearance.NEUTRAL} icon="Payment" isLoading={isLoading} />
                  )}
                  <CardContent data-cy="card-payment-method">
                    <CardLabel isLoading={isCardDetailLoading || isLoading}>Payment Method</CardLabel>
                    <CardValue isLoading={isCardDetailLoading || isLoading}>{hasPaymentMethod && activePaymentMethod ? activePaymentMethod : 'No Card on File'}</CardValue>
                    {cardDetail?.expDate && <CardExpiration date={cardDetail?.expDate} isLoading={isLoading} />}
                    {!hasPaymentMethod && <CardAction isLoading={isCardDetailLoading || isLoading} label="Add Payment Method" to={URL.SettingsPaymentMethod} />}
                  </CardContent>
                </>
              )}
            </Card>
          </StyledCardRow>
        </PageHeader>

        {!card && !isLoading && (
          <PendingBalance data-cy="add-card-section">
            <PendingBalanceCard
              ctaLabel="Add My Debit Card"
              ctaTo={URL.SettingsPaymentMethod}
              imageAlt="Money transfer illustration"
              imageLarge={PendingWalletLarge}
              imageSmall={PendingWalletSmall}
              label="Enter your debit card and get paid!"
              message="Add your bank debit card and receive tips right in your bank account."
            />
            <PendingBalanceCard
              bgColor="#21B670"
              ctaLabel="Get Started!"
              ctaTo={appConfig.LINKS.CHIME}
              externalTo
              imageAlt="Chime Card Illustration"
              imageLarge={PendingChimeLarge}
              imageSmall={PendingChimeSmall}
              label="Need a debit card? Sign up with Chime!"
              message="No monthly fee - Get paid early&sup1; - Fee-free overdraft&sup2;"
              footnote="Banking services and debit card provided by The Bancorp Bank or Stride Bank, N.A., Members FDIC."
            />
          </PendingBalance>
        )}

        <ListContext.Consumer>
          {({ methods, query, components }) => {
            const { pagination: Pager, resultsPerPage: ResultsPerPage } = components;
            const { onFiltersChange, onSortChange, isSortedBy, isSortDesc } = methods;
            const { q, sort } = query;
            const isDescendingSort = isSortDesc();
            const paymentDateSorted = isSortedBy('created_at');
            const statusSorted = isSortedBy('status');
            const amountSorted = isSortedBy('net');

            const filterPayoutDates = (dates: string[]) => {
              onFiltersChange([
                ...(q || []).reduce((acc: IApiQueryParam[], param: IApiQueryParam) => {
                  if (param.key !== 'payment_date') acc.push(param);
                  return acc;
                }, []),
                {
                  key: 'payment_date',
                  operator: ApiQueryOperator.gte,
                  value: formatDateForApi(dates[0]),
                },
                {
                  key: 'payment_date',
                  operator: ApiQueryOperator.lte,
                  value: formatDateForApi(dates[1]),
                },
              ]);
            };

            const onEmployerSearchChange = (siteId: string) => {
              const newEmployerIdSearch = {
                key: 'site_id',
                operator: ApiQueryOperator.eq,
                value: siteId,
              };

              onFiltersChange([
                ...(q || []).reduce((acc: IApiQueryParam[], param: IApiQueryParam) => {
                  if (param.key !== 'site_id') acc.push(param);
                  return acc;
                }, []),
                ...(siteId ? [newEmployerIdSearch] : []),
              ]);
            };

            const onStatusSearchChange = (newStatusSearchOption: DDOption[]) => {
              const mappedStatus = newStatusSearchOption.map(({ value }: DDOption) => value);
              const newStatusSearch = {
                key: 'status',
                operator: ApiQueryOperator.in,
                value: mappedStatus.join(','),
              };

              onFiltersChange([
                ...(q || []).reduce((acc: IApiQueryParam[], param: IApiQueryParam) => {
                  if (param.key !== 'status') acc.push(param);
                  return acc;
                }, []),
                ...(newStatusSearchOption.length > 0 ? [newStatusSearch] : []),
              ]);
            };
            const debouncedStatusChange = debounce(onStatusSearchChange, 500);

            const statusesFromUrl = (q || []).filter((item: IApiQueryParam) => item.key === 'status').map((qStatus: any) => qStatus.value);

            const siteIdFromUrl = (q || []).find((item: IApiQueryParam) => item.key === 'site_id');

            const statusOptions = [
              {
                label: capitalize(PayoutStatus.PENDING),
                value: PayoutStatus.PENDING,
              },
              {
                label: capitalize(PayoutStatus.EXPIRED),
                value: PayoutStatus.EXPIRED,
              },
              {
                label: capitalize(PayoutStatus.PAID),
                value: PayoutStatus.PAID,
              },
              {
                label: capitalize(PayoutStatus.PAYROLL),
                value: PayoutStatus.PAYROLL,
              },
              {
                label: capitalize(PayoutStatus.CANCELED),
                value: PayoutStatus.CANCELED,
              },
            ];
            const qWithoutDates = (q || []).filter((item: IApiQueryParam) => item.key !== 'payment_date');
            const startDate = getQueryStringValue(q, 'payment_date', ApiQueryOperator.gte) as string;
            const endDate = getQueryStringValue(q, 'payment_date', ApiQueryOperator.lte) as string;

            return (
              <>
                {!!employersForUser.length && <EmployersTable employersForUser={employersForUser} isLoading={isEmployersLoading} />}
                <Section afterHeaderCardRow>
                  <SectionHeader title="Payments" value={`(${payoutsTotal})`} isLoading={isPayoutsLoading} data-cy="payout-total-header">
                    <PayoutDateRange>
                      <Label>Date Range</Label>
                      <DateRangePicker
                        className="payout-date-range-picker"
                        data-cy="payout-date-range-picker"
                        fluidWidth={false}
                        inputHeight="small"
                        onSubmit={filterPayoutDates}
                        pickerPosition="right"
                        startDate={DEFAULT_START_DATE}
                        endDate={DEFAULT_END_DATE}
                        submitLabel="apply"
                      />
                    </PayoutDateRange>
                    <FilterButton filterActive={filterActive} appliedFilters={qWithoutDates.length} onClick={() => setFilterActive(!filterActive)} />
                    <ButtonExportCSVAsync data-cy="export-btn" disabled={isPayoutsLoading || !payouts || payouts?.length === 0} exportFunc={() => exportCSV(q, sort)} fileName={`Payments_${fullName}_${startDate}_${endDate}`} />
                  </SectionHeader>

                  <Filter isVisible={filterActive} data-cy="filter-section">
                    <FieldWrapper label="Employer">
                      <Select
                        placeholder="All"
                        multi={false}
                        options={siteOptions}
                        values={siteOptions.filter((so) => isEqual(so.value, siteIdFromUrl))}
                        onChange={(values) => {
                          onEmployerSearchChange(values?.[0]?.value === null ? '' : values?.[0]?.value);
                        }}
                        data-cy="site-select"
                      />
                    </FieldWrapper>

                    <FieldWrapper label="Status">
                      <Select
                        options={statusOptions}
                        values={statusOptions.filter((optionVal: { label: string; value: string }) => statusesFromUrl.includes(optionVal.value))}
                        multi
                        onChange={debouncedStatusChange}
                        data-cy="status-select"
                      />
                    </FieldWrapper>
                  </Filter>

                  <EmployeePayoutTable data-cy="payout-table">
                    <TableHead data-cy="payout-table-head">
                      <HeaderRow sticky>
                        <HeaderCell>
                          <SortLabel label="Date" onClick={() => onSortChange('created_at')} isSorted={paymentDateSorted} isSortedDesc={isDescendingSort} isLoading={isPayoutsLoading} data-cy="header-payment-date" />
                        </HeaderCell>

                        <HeaderCell>
                          <SortLabel label="Status" onClick={() => onSortChange('status')} isSorted={statusSorted} isSortedDesc={isDescendingSort} isLoading={isPayoutsLoading} data-cy="header-status" />
                        </HeaderCell>

                        <HeaderCell align="right">
                          <SortLabel label="Amount" isSorted={amountSorted} onClick={() => onSortChange('net')} isSortedDesc={isDescendingSort} isLoading={isPayoutsLoading} data-cy="header-amount" />
                        </HeaderCell>

                        <HeaderCell data-cy="header-payment-method">{isPayoutsLoading ? '' : 'Payment Method'}</HeaderCell>

                        <HeaderCell data-cy="header-employer">{isPayoutsLoading ? '' : 'Employer'}</HeaderCell>

                        <HeaderCell data-cy="header-note">{isPayoutsLoading ? '' : 'Note'}</HeaderCell>
                      </HeaderRow>
                    </TableHead>

                    <TableBody data-cy="payout-table-body">
                      {isPayoutsLoading
                        ? [...Array(query?.limit)].map((e, i) => <EmployeePayoutTableRow key={i} isLoading />)
                        : payouts?.map((payout: IPayout, index: number) => (
                            <EmployeePayoutTableRow
                              data-cy="payout-row"
                              data-value={payout.id}
                              key={`dashboard-payouts-${index}`}
                              amount={centsToDollarsDisplay(payout.net)}
                              date={formatDateForUI(payout.createdAt)}
                              employer={payout.site.name}
                              isLoading={isPayoutsLoading}
                              note={payout.notes}
                              paymentMethod={payout.paymentDestination?.last4}
                              paymentMethodType={payout.paymentDestination?.network}
                              status={payout.status}
                              time={getTimeWithZone(payout.createdAt)}
                              alert={payout.alert}
                              payoutId={payout.id}
                            />
                          ))}
                      {!isPayoutsLoading && payouts && payouts.length === 0 ? (
                        <EmptyTable>
                          <Empty icon="Payment" message="No payments found." />
                        </EmptyTable>
                      ) : null}
                    </TableBody>
                  </EmployeePayoutTable>

                  <EmployeePayoutList>
                    {isPayoutsLoading
                      ? [...Array(query?.limit)].map((e, i) => <EmployeePayoutCard key={i} isLoading />)
                      : payouts?.map((payout: IPayout, index: number) => (
                          <EmployeePayoutCard
                            alert={payout.alert}
                            amount={centsToDollarsDisplay(payout.net)}
                            data-cy="payout-card"
                            data-value={payout.id}
                            key={`dashboard-payouts-${index}`}
                            date={formatDateForUI(payout.createdAt)}
                            employer={payout.site.name}
                            isLoading={isPayoutsLoading}
                            note={payout.notes}
                            paymentMethod={payout.paymentDestination?.last4}
                            paymentMethodType={payout.paymentDestination?.network}
                            status={payout.status}
                            time={getTimeWithZone(payout.createdAt)}
                            payoutId={payout.id}
                          />
                        ))}
                  </EmployeePayoutList>

                  {payouts && payouts.length !== 0 && (
                    <PaginationFooter>
                      {!isPayoutsLoading && payouts && payoutsTotal && payoutsTotal > query.limit && (
                        <>
                          {ResultsPerPage}
                          {Pager}
                        </>
                      )}
                    </PaginationFooter>
                  )}
                </Section>
              </>
            );
          }}
        </ListContext.Consumer>
      </ListContextProvider>
    </>
  );
};

export default EmployeeDashboard;
